import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRightLeft, Info, Loader2, MessageSquare, User } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// "Já existe um tíquete aberto para este contato" — e acabava aí.
//
// A mensagem era verdadeira e inútil: não dizia com quem o contato está, em
// que fila, nem o que fazer. Quem precisava falar com o cliente tinha que sair
// perguntando pela equipe quem estava com ele. Aqui a tela responde as três
// coisas e oferece o caminho: pedir a transferência a quem está atendendo.
const TicketEmAtendimentoModal = ({ aberto, aoFechar, atendimento }) => {
	const navigate = useNavigate();
	const { user } = useContext(AuthContext);
	const ehAdmin = user?.profile === "admin";
	const [pedindo, setPedindo] = useState(false);
	const [pedido, setPedido] = useState(false);

	if (!atendimento) return null;

	const solicitar = async () => {
		setPedindo(true);

		try {
			await api.post("/transfer-requests", { ticketId: atendimento.id });
			setPedido(true);
		} catch (err) {
			toastError(err);
		}

		setPedindo(false);
	};

	const fechar = () => {
		setPedido(false);
		aoFechar();
	};

	return (
		<Dialog open={aberto} onOpenChange={(estado) => !estado && fechar()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<ArrowRightLeft className="h-4 w-4 text-amber-500" />
						Ticket em atendimento
					</DialogTitle>
				</DialogHeader>

				<p className="text-sm text-muted-foreground">
					Este contato já possui um ticket em aberto com outro atendente.
				</p>

				<div className="space-y-1.5 rounded-lg border bg-muted/40 p-3 text-sm">
					<div className="flex items-center justify-between gap-3">
						<span className="text-muted-foreground">Contato</span>
						<span className="truncate font-medium">
							{atendimento.contact?.name || "—"}
						</span>
					</div>
					<div className="flex items-center justify-between gap-3">
						<span className="text-muted-foreground">Atendente</span>
						<span className="truncate font-medium">
							{atendimento.user?.name || "Ninguém (aguardando)"}
						</span>
					</div>
					<div className="flex items-center justify-between gap-3">
						<span className="text-muted-foreground">Fila</span>
						<span className="truncate font-medium">
							{atendimento.queue?.name || "Sem fila"}
						</span>
					</div>
					<div className="flex items-center justify-between gap-3">
						<span className="text-muted-foreground">Conexão</span>
						<span className="truncate font-medium">
							{atendimento.whatsapp?.name || "—"}
						</span>
					</div>
					<div className="flex items-center justify-between gap-3">
						<span className="text-muted-foreground">Ticket</span>
						<span className="font-medium">#{atendimento.id}</span>
					</div>
				</div>

				{pedido && (
					<div className="flex gap-2 rounded-lg border border-sky-500/40 bg-sky-500/10 p-3 text-sm">
						<Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" />
						<p>
							Uma solicitação de transferência foi enviada ao atendente.
							Feche esta janela e aguarde: você será notificado quando o
							contato for transferido para você.
						</p>
					</div>
				)}

				<div className="flex flex-wrap justify-end gap-2">
					{/* "Abrir a conversa" (entrar no atendimento ativo de outro
					    agente) fica só para admin/supervisor — quem precisa
					    intervir. Agente comum não tem esse atalho: o caminho é
					    solicitar a transferência e o atendente atual aprovar,
					    por segurança/privacidade do atendimento em andamento. */}
					{ehAdmin && (
						<Button
							variant="ghost"
							className="gap-2"
							onClick={() => {
								fechar();
								navigate(`/tickets/${atendimento.id}`);
							}}
						>
							<MessageSquare className="h-4 w-4" />
							Abrir a conversa
						</Button>
					)}

					{pedido ? (
						<Button onClick={fechar}>Entendi, aguardar transferência</Button>
					) : (
						<Button className="gap-2" onClick={solicitar} disabled={pedindo}>
							{pedindo ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<User className="h-4 w-4" />
							)}
							Solicitar transferência
						</Button>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default TicketEmAtendimentoModal;
